import {
  ArrowDownIcon,
  ArrowUpIcon,
  BoltIcon,
  BoltSlashIcon,
  EyeIcon,
  EyeSlashIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { SaveChanges } from 'components/SaveChanges'
import type { InputType } from 'config'
import { useEffect, useMemo, useState } from 'react'
import { Prompt } from 'react-router-dom'
import { toast } from 'react-toastify'
import { getAdminConfig, setAdminConfig } from 'services'
import { Button, ButtonGroup, Tooltip } from 'stories/components'
import { confirm, prompt } from 'utils'

import { CustomFieldModal } from './CustomFieldModal'
import {
  applySectionOrder,
  buildCustomSections,
  IServicingInfoConfig,
  IServicingInfoCustomField,
  IServicingInfoSection,
  parseSections,
  SERVICING_INFO_TABS,
  ServicingInfoTab,
} from './types'

const defaultConfig: IServicingInfoConfig = {
  fields: {},
  sections: {},
  sectionOrder: [],
  customSections: [],
}

interface ApiResponse {
  data: IServicingInfoConfig
  inputs: Record<string, InputType>
  insuranceInputs: Record<string, InputType>
  taxInputs: Record<string, InputType>
  extensionInputs: Record<string, InputType>
}

export const ServicingInformationConfig = () => {
  const [isLoading, setLoading] = useState(true)
  const [isChanged, setChanged] = useState(false)
  const [tab, setTab] = useState<ServicingInfoTab>('Loan Details')
  const [data, setData] = useState<IServicingInfoConfig>(defaultConfig)
  const [inputs, setInputs] = useState<Record<string, InputType>>({})
  const [editingField, setEditingField] = useState<{
    sectionId: string
    field: IServicingInfoCustomField | null
  } | null>(null)

  useEffect(() => {
    setLoading(true)
    getAdminConfig('servicingInfoConfig')
      .then((res: ApiResponse) => {
        const cfg = (res?.data || {}) as IServicingInfoConfig
        setData({
          fields: cfg.fields || {},
          sections: cfg.sections || {},
          sectionOrder: cfg.sectionOrder || [],
          customSections: cfg.customSections || [],
        })
        setInputs(res?.inputs || {})
      })
      .finally(() => setLoading(false))
  }, [])

  /**
   * All sections (built-in + custom) in display order, then filtered to the
   * currently-selected tab.
   */
  const sectionsForTab = useMemo<IServicingInfoSection[]>(() => {
    if (!Object.keys(inputs).length) return []
    const builtIn = parseSections(inputs)
    const custom = buildCustomSections(data.customSections || [])
    const all = applySectionOrder([...builtIn, ...custom], data.sectionOrder || [])
    return all.filter((s) => (s.isCustom ? s.tab === tab : s.tab === tab))
  }, [inputs, data.customSections, data.sectionOrder, tab])

  const allOrderedKeys = useMemo<string[]>(() => {
    if (!Object.keys(inputs).length) return []
    const builtIn = parseSections(inputs)
    const custom = buildCustomSections(data.customSections || [])
    return applySectionOrder([...builtIn, ...custom], data.sectionOrder || []).map((s) => s.key)
  }, [inputs, data.customSections, data.sectionOrder])

  const markChanged = (next: IServicingInfoConfig) => {
    setData(next)
    setChanged(true)
  }

  const onToggleField = (fieldKey: string, prop: 'visible' | 'required', currentValue: boolean) => {
    const next = cloneDeep(data)
    if (!next.fields) next.fields = {}
    if (!next.fields[fieldKey]) next.fields[fieldKey] = {}
    next.fields[fieldKey][prop] = !currentValue
    markChanged(next)
  }

  const onRenameField = async (fieldKey: string, currentTitle: string) => {
    const value = (await prompt('Enter new field title', { value: currentTitle })) as any
    if (value === false || value === undefined) return
    const next = cloneDeep(data)
    if (!next.fields) next.fields = {}
    if (!next.fields[fieldKey]) next.fields[fieldKey] = {}
    const trimmed = (value || '').trim()
    if (!trimmed) {
      delete next.fields[fieldKey].title
    } else {
      next.fields[fieldKey].title = trimmed
    }
    markChanged(next)
  }

  const onRenameSection = async (sectionKey: string, currentTitle: string, isCustom: boolean) => {
    const value = (await prompt('Enter new section title', { value: currentTitle })) as any
    if (value === false || value === undefined) return
    const trimmed = (value || '').trim()
    const next = cloneDeep(data)
    if (isCustom) {
      const id = sectionKey.replace(/^custom:/, '')
      if (!next.customSections) next.customSections = []
      const sec = next.customSections.find((s) => s.id === id)
      if (sec) sec.title = trimmed || 'Untitled'
    } else {
      if (!next.sections) next.sections = {}
      if (!next.sections[sectionKey]) next.sections[sectionKey] = {}
      if (!trimmed) {
        delete next.sections[sectionKey].title
      } else {
        next.sections[sectionKey].title = trimmed
      }
    }
    markChanged(next)
  }

  /**
   * Move a section up/down within its tab. We swap the section's position with
   * the nearest sibling in the same tab, then persist the new global order.
   */
  const onMoveSection = (sectionKey: string, direction: -1 | 1) => {
    const tabKeys = sectionsForTab.map((s) => s.key)
    const indexInTab = tabKeys.indexOf(sectionKey)
    if (indexInTab === -1) return
    const swapIndexInTab = indexInTab + direction
    if (swapIndexInTab < 0 || swapIndexInTab >= tabKeys.length) return

    const otherKey = tabKeys[swapIndexInTab]
    const next = cloneDeep(data)
    const order = [...allOrderedKeys]
    const i = order.indexOf(sectionKey)
    const j = order.indexOf(otherKey)
    if (i === -1 || j === -1) return
    ;[order[i], order[j]] = [order[j], order[i]]
    next.sectionOrder = order
    markChanged(next)
  }

  const onAddCustomSection = async () => {
    const value = (await prompt('Enter section title', { value: '' })) as any
    if (value === false || value === undefined) return
    const trimmed = (value || '').trim()
    if (!trimmed) {
      toast('Title is required', { type: 'warning' })
      return
    }
    const next = cloneDeep(data)
    if (!next.customSections) next.customSections = []
    const id = `${Date.now()}`
    next.customSections.push({
      id,
      title: trimmed,
      tab,
      fields: [],
    })
    if (!next.sectionOrder) next.sectionOrder = []
    next.sectionOrder.push(`custom:${id}`)
    markChanged(next)
  }

  const onDeleteCustomSection = async (sectionKey: string) => {
    const isContinue = await confirm('Delete this section and all its custom fields? This cannot be undone.')
    if (!isContinue) return
    const id = sectionKey.replace(/^custom:/, '')
    const next = cloneDeep(data)
    next.customSections = (next.customSections || []).filter((s) => s.id !== id)
    next.sectionOrder = (next.sectionOrder || []).filter((k) => k !== sectionKey)
    markChanged(next)
  }

  const onSaveCustomField = (sectionId: string, field: IServicingInfoCustomField) => {
    const next = cloneDeep(data)
    const section = (next.customSections || []).find((s) => s.id === sectionId)
    if (!section) return
    const existingIdx = section.fields.findIndex((f) => f.key === field.key)
    if (existingIdx >= 0) section.fields[existingIdx] = field
    else section.fields.push(field)
    markChanged(next)
    setEditingField(null)
  }

  const onDeleteCustomField = async (sectionId: string, fieldKey: string) => {
    const isContinue = await confirm('Delete this custom field?')
    if (!isContinue) return
    const next = cloneDeep(data)
    const section = (next.customSections || []).find((s) => s.id === sectionId)
    if (!section) return
    section.fields = section.fields.filter((f) => f.key !== fieldKey)
    markChanged(next)
  }

  const onSubmit = async () => {
    setLoading(true)
    try {
      await setAdminConfig('servicingInfoConfig', data as any)
      toast('Servicing Information configuration saved.', { type: 'info' })
      setChanged(false)
    } finally {
      setLoading(false)
    }
  }

  const isFieldVisible = (key: string): boolean => data.fields?.[key]?.visible !== false
  const isFieldRequired = (key: string): boolean => !!data.fields?.[key]?.required
  const getFieldTitle = (key: string, fallback: string): string => data.fields?.[key]?.title || fallback
  const getSectionTitle = (sectionKey: string, fallback: string): string =>
    data.sections?.[sectionKey]?.title || fallback

  const renderFieldRow = (
    sectionKey: string,
    fieldKey: string,
    originalTitle: string,
    isCustom: boolean,
    customSectionId: string | null,
    customField: IServicingInfoCustomField | null,
  ) => {
    const visible = isFieldVisible(fieldKey)
    const required = isFieldRequired(fieldKey)
    const title = getFieldTitle(fieldKey, originalTitle)
    const VisibleIcon = visible ? EyeIcon : EyeSlashIcon
    const RequiredIcon = required ? BoltIcon : BoltSlashIcon

    return (
      <div className="flex justify-between items-center py-1" key={`${sectionKey}-${fieldKey}`}>
        <p className={`text-sm ${visible ? '' : 'line-through opacity-60'}`}>{title}</p>
        <div className="flex gap-2 items-center">
          <Tooltip message={visible ? 'Hide field' : 'Show field'}>
            <span className="btn-icon" onClick={() => onToggleField(fieldKey, 'visible', visible)}>
              <VisibleIcon className="w-4 h-4" />
            </span>
          </Tooltip>
          <Tooltip message={required ? 'Required' : 'Not Required'}>
            <span className="btn-icon" onClick={() => onToggleField(fieldKey, 'required', required)}>
              <RequiredIcon className="w-4 h-4" />
            </span>
          </Tooltip>
          <Tooltip message="Rename field">
            <span className="btn-icon" onClick={() => onRenameField(fieldKey, title)}>
              <PencilSquareIcon className="w-4 h-4" />
            </span>
          </Tooltip>
          {isCustom && customSectionId && customField && (
            <>
              <Tooltip message="Edit field type/options">
                <span
                  className="btn-icon"
                  onClick={() => setEditingField({ sectionId: customSectionId, field: customField })}
                >
                  <PencilSquareIcon className="w-4 h-4 text-shade-blue" />
                </span>
              </Tooltip>
              <Tooltip message="Delete custom field">
                <span className="btn-icon" onClick={() => onDeleteCustomField(customSectionId, customField.key)}>
                  <TrashIcon className="w-4 h-4 text-red-500" />
                </span>
              </Tooltip>
            </>
          )}
        </div>
      </div>
    )
  }

  const renderSection = (section: IServicingInfoSection, index: number, totalInTab: number) => {
    const sectionTitle = section.isCustom ? section.title : getSectionTitle(section.key, section.title)
    const customSection = section.isCustom
      ? data.customSections?.find((s) => s.id === section.key.replace(/^custom:/, ''))
      : null

    return (
      <div key={section.key} className="mb-6 border border-gray-200 rounded p-3">
        <div className="flex items-center justify-between border-b border-shade-blue mb-2 pb-1">
          <p className="text-base font-semibold flex items-center gap-2 italic">
            {index + 1}. {sectionTitle}
            {section.isCustom && (
              <span className="text-xs not-italic text-shade-blue bg-blue-50 px-1.5 py-0.5 rounded">Custom</span>
            )}
          </p>
          <div className="flex gap-2 items-center">
            <Tooltip message="Rename section">
              <span className="btn-icon" onClick={() => onRenameSection(section.key, sectionTitle, section.isCustom)}>
                <PencilSquareIcon className="w-4 h-4" />
              </span>
            </Tooltip>
            {index > 0 && (
              <Tooltip message="Move up">
                <span className="btn-icon" onClick={() => onMoveSection(section.key, -1)}>
                  <ArrowUpIcon className="w-4 h-4" />
                </span>
              </Tooltip>
            )}
            {index < totalInTab - 1 && (
              <Tooltip message="Move down">
                <span className="btn-icon" onClick={() => onMoveSection(section.key, 1)}>
                  <ArrowDownIcon className="w-4 h-4" />
                </span>
              </Tooltip>
            )}
            {section.isCustom && customSection && (
              <>
                <Tooltip message="Add field in this section">
                  <span
                    className="btn-icon"
                    onClick={() => setEditingField({ sectionId: customSection.id, field: null })}
                  >
                    <PlusIcon className="w-4 h-4" />
                  </span>
                </Tooltip>
                <Tooltip message="Delete section">
                  <span className="btn-icon !text-red-500" onClick={() => onDeleteCustomSection(section.key)}>
                    <TrashIcon className="w-4 h-4" />
                  </span>
                </Tooltip>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
          {section.fieldKeys.length === 0 && (
            <p className="text-xs text-gray-400 italic">No fields in this section yet.</p>
          )}
          {section.isCustom && customSection
            ? customSection.fields.map((f) => renderFieldRow(section.key, f.key, f.title, true, customSection.id, f))
            : section.fieldKeys.map((fk) => {
                const input = inputs[fk]
                if (!input) return null
                if ((input.inputType as any) === 'section') return null
                const originalTitle =
                  (input.title as string) ||
                  ((input as any).inputType === 'group' ? `(${(input as any).inputType})` : fk)
                return renderFieldRow(section.key, fk, originalTitle, false, null, null)
              })}
        </div>
      </div>
    )
  }

  const totalInTab = sectionsForTab.length

  return (
    <div className="relative">
      <LayoutLoading show={isLoading} />
      <Prompt when={isChanged} message="You've made some changes!\nAre you sure want to leave without Saving?" />

      <div className="mt-2 mb-4">
        <ButtonGroup title={SERVICING_INFO_TABS} value={tab} onChange={(v: any) => setTab(v)} />
      </div>

      <div>
        {sectionsForTab.map((section, index) => renderSection(section, index, totalInTab))}
        {!sectionsForTab.length && !isLoading && (
          <p className="text-sm text-gray-500 italic">No sections in this tab.</p>
        )}
      </div>

      <div className="flex items-center gap-4 mt-4">
        <Button link onClick={onAddCustomSection}>
          + Add New Section
        </Button>
      </div>

      <div className="flex flex-row justify-center gap-4 mt-4">
        <Button onClick={onSubmit} loading={isLoading} disabled={!isChanged}>
          Save
        </Button>
      </div>

      <SaveChanges show={isChanged} label="Save Changes" onSave={onSubmit} />

      {editingField && (
        <CustomFieldModal
          field={editingField.field}
          existingKeys={
            (data.customSections || []).find((s) => s.id === editingField.sectionId)?.fields.map((f) => f.key) || []
          }
          onClose={() => setEditingField(null)}
          onSave={(field) => onSaveCustomField(editingField.sectionId, field)}
        />
      )}
    </div>
  )
}
